import { API_INSTANCE, fullBuildURL } from 'src/features/utils';
import type { Event, EventResponse, Ticket, EventHost, TeamMemberType } from 'src/types/types';

export const fetchEventsApi = async (params?: {
  limit?: number;
  offset?: number;
  status?: string[];
}) => {
  const queryParams = new URLSearchParams();
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.offset) queryParams.append('offset', params.offset.toString());
  if (params?.status && params.status.length > 0) {
    queryParams.append('status', params.status.join(','));
  }

  const url = queryParams.toString()
    ? `/api/v1/dashboard/events?${queryParams.toString()}`
    : `/api/v1/dashboard/events`;

  return API_INSTANCE.get<EventResponse[]>(url);
};

export const fetchEventsBySiteApi = async (siteId: string) =>
  await API_INSTANCE.get<EventResponse[]>(fullBuildURL(`/api/v1/dashboard/events/site/${siteId}`));

export const fetchEventApi = async (eventId: string) =>
  await API_INSTANCE.get<EventResponse>(fullBuildURL(`/api/v1/dashboard/events/${eventId}`));

export const updateEventApi = async (event: Event) =>
  await API_INSTANCE.put(fullBuildURL(`/api/v1/dashboard/events/${event.id}`), event);

export const createEventApi = async (event: Event) =>
  await API_INSTANCE.post(fullBuildURL(`/api/v1/dashboard/events`), event);

export const deleteEventApi = async (eventId: string) =>
  await API_INSTANCE.delete(fullBuildURL(`/api/v1/dashboard/events/${eventId}`));

export const copyEventApi = async (data: { id: string; checkboxSelections: any }) =>
  await API_INSTANCE.post(
    fullBuildURL(`/api/v1/dashboard/events/${data.id}/copy`),
    data.checkboxSelections,
  );

export const createAIEventApi = async (event: Event) =>
  await API_INSTANCE.post(fullBuildURL(`/api/v1/dashboard//openai/events`), event);

export const generateAITextApi = async (data: any) =>
  await API_INSTANCE.post(fullBuildURL(`/api/v1/dashboard/openai/generate`), data);

export const fetchEventTicketsApi = async (eventId: string) =>
  await API_INSTANCE.get(fullBuildURL(`/api/v1/dashboard/tickets/events/${eventId}`));

export const fetchEventTicketsWithoutMetricsApi = async (eventId: string) =>
  await API_INSTANCE.get(
    fullBuildURL(`/api/v1/dashboard/tickets/events/${eventId}`, `&ignore_real_time_metric=true`),
  );

export const createEventTicketsApi = async (ticket: Ticket) =>
  await API_INSTANCE.post(
    fullBuildURL(`/api/v1/dashboard/tickets/events/${ticket.event_id}`),
    ticket,
  );

export const updateEventTicketsApi = async (ticket: Ticket) =>
  await API_INSTANCE.put(fullBuildURL(`/api/v1/dashboard/tickets/${ticket.id}`), ticket);

export const updateEventTicketsOrderApi = async (ticket: Ticket) =>
  await API_INSTANCE.put(fullBuildURL(`/api/v1/dashboard/tickets/bulk/order`), ticket);

export const deleteEventTicketsApi = async (id: string) =>
  await API_INSTANCE.delete(fullBuildURL(`/api/v1/dashboard/tickets/${id}`));

export const fetchEventOccurrencesApi = async (event: Event) =>
  await API_INSTANCE.get(fullBuildURL(`/api/v1/dashboard/events/${event.id}/occurrences`), event);

export const fetchEventLeafOccurrencesApi = async (event: Event) =>
  await API_INSTANCE.get(
    fullBuildURL(`/api/v1/dashboard/events/${event.id}/leaf-occurrences`),
    event,
  );

export const updateEventOccurrenceApi = async (event: Event) =>
  await API_INSTANCE.put(fullBuildURL(`/api/v1/dashboard/events/${event.id}/occurrences`), event);

export const createEventOccurrenceApi = async (event: Event) =>
  await API_INSTANCE.post(fullBuildURL(`/api/v1/dashboard/events/${event.id}/occurrences`), event);

export const createDateWithTimesEventOccurrenceApi = async (event: Event) =>
  await API_INSTANCE.post(
    fullBuildURL(`/api/v1/dashboard/events/${event.id}/time-based-occurrences`),
    event,
  );

export const updateDateWithTimesEventOccurrenceApi = async (event: Event) =>
  await API_INSTANCE.put(
    fullBuildURL(`/api/v1/dashboard/events/${event.id}/time-based-occurrences`),
    event,
  );

export const deleteDateWithTimesEventOccurrenceApi = async (event: Event) =>
  await API_INSTANCE.delete(
    fullBuildURL(`/api/v1/dashboard/events/${event.id}/time-based-occurrences`),
    event,
  );

export const fetchEventPromosApi = async (eventId: string) =>
  await API_INSTANCE.get(fullBuildURL(`/api/v1/dashboard/event-promos/events/${eventId}`));

export const createEventPromosApi = async (promo: any) =>
  await API_INSTANCE.post(
    fullBuildURL(`/api/v1/dashboard/event-promos/events/${promo.event_id}`),
    promo,
  );

export const updateEventPromosApi = async (promo: any) =>
  await API_INSTANCE.put(
    fullBuildURL(`/api/v1/dashboard/event-promos/${promo.id}/events/${promo.event_id}`),
    promo,
  );

export const deleteEventPromoApi = async (promo: any) =>
  await API_INSTANCE.delete(
    fullBuildURL(`/api/v1/dashboard/event-promos/${promo.id}/events/${promo.event_id}`),
    promo,
  );

export const updateEventWaitlistApi = async (data: any) =>
  await API_INSTANCE.put(fullBuildURL(`/api/v1/dashboard/events/${data.event_id}/waitlist`), data);

export const notifyWaitlistSubscribersApi = async (data: any) =>
  await API_INSTANCE.put(
    fullBuildURL(`/api/v1/dashboard/events/${data.event_id}/waitlist/notify`),
    data,
  );
export const createPreapprovedEventAPI = async (event: Event) =>
  await API_INSTANCE.post(fullBuildURL(`/api/v1/dashboard/events/public/preapprove-event`), event);
export const submitPreapprovedEventAPI = async (event: Event) =>
  await API_INSTANCE.put(
    fullBuildURL(`/api/v1/dashboard/events/public/${event.id}/preapprove-event-submit`),
    event,
  );

// Event Hosts API
export const fetchEventHostsApi = async (eventId: string) =>
  await API_INSTANCE.get<EventHost[]>(fullBuildURL(`/api/v1/dashboard/events/${eventId}/hosts`));

export const createEventHostApi = async (data: {
  eventId: string;
  memberId: string;
  hostData?: { active?: boolean; first_name?: string; last_name?: string };
}) =>
  await API_INSTANCE.post<EventHost>(
    fullBuildURL(`/api/v1/dashboard/events/${data.eventId}/hosts/${data.memberId}`),
    data.hostData || {},
  );

export const updateEventHostApi = async (data: {
  eventId: string;
  memberId: string;
  hostData: { active?: boolean; first_name?: string; last_name?: string; role?: string };
}) =>
  await API_INSTANCE.patch<EventHost>(
    fullBuildURL(`/api/v1/dashboard/events/${data.eventId}/hosts/${data.memberId}`),
    data.hostData,
  );

export const deleteEventHostApi = async (data: { eventId: string; memberId: string }) =>
  await API_INSTANCE.delete(
    fullBuildURL(`/api/v1/dashboard/events/${data.eventId}/hosts/${data.memberId}`),
  );

export const inviteTeamMemberAsHostApi = async (data: {
  siteId: string;
  eventId: string;
  memberData: { email: string; first_name: string; last_name: string; role: string };
}) =>
  await API_INSTANCE.post<{ member: TeamMemberType; event_host?: EventHost }>(
    fullBuildURL(`/api/v1/dashboard/sites/${data.siteId}/teams`),
    { ...data.memberData, event_id: data.eventId },
  );

export const addEventHostDirectApi = async (data: {
  eventId: string;
  hostData: { email: string; first_name: string; last_name: string; role: string };
}) =>
  await API_INSTANCE.post<EventHost>(
    fullBuildURL(`/api/v1/dashboard/events/${data.eventId}/hosts`),
    data.hostData,
  );
